import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Form
} from 'react-bootstrap';
import {
  FaChartBar,
  FaDatabase,
  FaSearch,
  FaArrowRight,
  FaUsers,
  FaBuilding,
  FaUser,
  FaEnvelope,
  FaCalendar,
  FaTag,
  FaFileAlt,
  FaChartLine,
  FaShoppingCart,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaSchool,
  FaMoneyBillWave,
  FaTasks,
  FaProjectDiagram,
  FaHistory,
  FaPaperclip,
  FaCalendarCheck,
  FaChevronDown,
  FaChevronRight
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import {
  getUserFieldPermissionsByObject,
  getFlsReadDeniedLowerSet,
  resolveReadableObjectNamesLower,
  filterReportTypesByObjectPermissions,
  canReadObjectForReporting,
} from '../../utils/permissionCache';
import './css/ReportBuilder.css';

const ReportBuilder = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [objects, setObjects] = useState([]);
  const [relatedLists, setRelatedLists] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableObjects, setAvailableObjects] = useState([]);
  const [lookupFields, setLookupFields] = useState([]);
  const [expandedLookupGroups, setExpandedLookupGroups] = useState({});

  // Handle cancel button click
  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      // Navigate to reports list page
      navigate('/reports');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Use the new ReportBuilder API endpoint
      const reportBuilderApi = new DataApi('reportbuilder/report-types');
      const response = await reportBuilderApi.fetchAll();

      if (response?.data?.success) {
        let reportTypes = response.data.data || [];
        try {
          const readableLower = await resolveReadableObjectNamesLower();
          reportTypes = filterReportTypesByObjectPermissions(reportTypes, readableLower);
        } catch (permErr) {
          console.warn('Report types permission filter failed:', permErr);
        }
        console.log('Loaded report types:', reportTypes);

        // Debug: Check if target_field_name is present in the response
        const relationshipTypes = reportTypes.filter(type => type.type === 'relationship');
        console.log('Relationship types from API:', relationshipTypes);
        relationshipTypes.forEach(type => {
          console.log(`Type: ${type.source_object_name} -> ${type.target_object_name}`);
          console.log(`Source field: ${type.source_field_name}`);
          console.log(`Target field: ${type.target_field_name}`);
        });

        // Check for duplicate IDs
        const ids = reportTypes.map(type => type.id);
        const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
        if (duplicateIds.length > 0) {
          console.warn('Duplicate IDs found:', duplicateIds);
          console.warn('Report types with duplicate IDs:', reportTypes.filter(type => duplicateIds.includes(type.id)));
        }

        setReportTypes(reportTypes);

        // Extract objects and related lists from report types
        const objects = reportTypes
          .filter(type => type.type === 'individual')
          .map(type => ({
            object_name: type.object_name,
            object_label: type.object_label,
            allow_activity: type.allow_activity,
            is_standard: type.is_standard
          }));

        const relatedLists = reportTypes
          .filter(type => type.type === 'relationship')
          .map(type => ({
            source_object_name: type.source_object_name,
            target_object_name: type.target_object_name,
            relationship_name: type.relationship_name,
            relationship_label: type.relationship_label,
            relationship_type: type.relationship_type,
            fields: type.fields,
            actions: type.actions
          }));

        setObjects(objects);
        setRelatedLists(relatedLists);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      setError('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };


  const getObjectIcon = (objectName, reportType = null) => {
    const name = (objectName || reportType?.object_name || reportType?.source_object_name || '').toLowerCase();
    const label = (reportType?.description || reportType?.object_label || '').toLowerCase();

    if (name.includes('history') || label.includes('history')) return FaHistory;
    if (name.includes('activity') || label.includes('activity')) return FaTasks;
    if (name.includes('attachment') || name.includes('file') || label.includes('attachment')) return FaPaperclip;
    if (name.includes('attendee') || label.includes('attendee')) return FaCalendarCheck;

    if (!name) return FaDatabase;

    const iconMap = {
      contact: FaUser,
      account: FaBuilding,
      lead: FaUser,
      student: FaUserGraduate,
      class: FaChalkboardTeacher,
      department: FaSchool,
      employee: FaUsers,
      company: FaBuilding,
      business: FaBuilding,
      campaign: FaChartLine,
      product: FaShoppingCart,
      pricebook: FaMoneyBillWave,
      task: FaTasks,
      incident: FaFileAlt,
      message: FaEnvelope,
      notification: FaEnvelope,
      workflow: FaProjectDiagram,
      report: FaChartLine,
      dashboard: FaChartLine,
      fee: FaMoneyBillWave,
      payment: FaMoneyBillWave,
      file: FaPaperclip,
      tags: FaTag,
      recordtype: FaTag,
      role: FaUsers,
      user: FaUser,
      group: FaUsers,
      recordview: FaFileAlt
    };

    if (iconMap[name]) return iconMap[name];

    if (name.includes('contact')) return FaUser;
    if (name.includes('account')) return FaBuilding;
    if (name.includes('lead')) return FaUser;
    if (name.includes('student')) return FaUserGraduate;
    if (name.includes('class')) return FaChalkboardTeacher;
    if (name.includes('department')) return FaSchool;
    if (name.includes('employee')) return FaUsers;
    if (name.includes('company') || name.includes('business')) return FaBuilding;
    if (name.includes('campaign')) return FaChartLine;
    if (name.includes('product')) return FaShoppingCart;
    if (name.includes('price') || name.includes('fee') || name.includes('payment')) return FaMoneyBillWave;
    if (name.includes('task')) return FaTasks;
    if (name.includes('message') || name.includes('notification')) return FaEnvelope;
    if (name.includes('workflow')) return FaProjectDiagram;
    if (name.includes('report') || name.includes('dashboard')) return FaChartLine;
    if (name.includes('attachment') || name.includes('file')) return FaPaperclip;
    if (name.includes('attendee')) return FaCalendarCheck;
    if (name.includes('history')) return FaHistory;
    if (name.includes('activity')) return FaTasks;
    if (name.includes('tag') || name.includes('recordtype')) return FaTag;
    if (name.includes('role') || name.includes('group') || name.includes('user')) return FaUsers;

    return FaDatabase;
  };

  const getDisplayTitle = (reportType) => {
    const raw = reportType?.description || reportType?.object_label || '';
    return raw.replace(/\s*\(as an individual\)\s*/i, '').trim() || raw;
  };

  const getReportCategory = (reportType) => {
    const name = (reportType?.object_name || reportType?.source_object_name || '').toLowerCase();
    const label = (reportType?.description || '').toLowerCase();

    if (reportType?.type === 'relationship') {
      return { label: 'JOINT', variant: 'joint' };
    }
    if (name.includes('history') || label.includes('history')) {
      return { label: 'HISTORY', variant: 'history' };
    }
    if (name.includes('activity') || label.includes('activity')) {
      return { label: 'ACTIVITY', variant: 'activity' };
    }
    if (name.includes('attachment') || name.includes('file') || label.includes('attachment')) {
      return { label: 'FILES', variant: 'files' };
    }
    if (name.includes('attendee') || label.includes('attendee')) {
      return { label: 'PARTICIPANTS', variant: 'participants' };
    }
    if (reportType?.is_standard) {
      return { label: 'STANDARD', variant: 'standard' };
    }
    return { label: 'CUSTOM', variant: 'custom' };
  };

  const groupLookupFieldsByReference = (fields) => {
    const groups = {};
    (fields || []).forEach((field) => {
      const ref = field.reference_object || 'Other';
      if (!groups[ref]) groups[ref] = [];
      groups[ref].push(field);
    });
    return groups;
  };

  const toggleLookupGroup = (groupName) => {
    setExpandedLookupGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const getCategoryAccentColor = (variant) => {
    const colors = {
      standard: '#16a34a',
      custom: '#7c3aed',
      joint: '#ea580c',
      history: '#2563eb',
      activity: '#7c3aed',
      files: '#64748b',
      participants: '#ea580c'
    };
    return colors[variant] || '#1e3a8a';
  };

  const getObjectColor = (objectName, reportType = null) => {
    const category = reportType ? getReportCategory(reportType) : null;
    if (category) return getCategoryAccentColor(category.variant);

    const lowerName = (objectName || '').toLowerCase();
    if (lowerName.includes('history')) return '#2563eb';
    if (lowerName.includes('activity')) return '#7c3aed';
    if (lowerName.includes('attachment') || lowerName.includes('file')) return '#64748b';
    if (lowerName.includes('attendee')) return '#ea580c';
    if (lowerName.includes('account')) return '#16a34a';
    return '#1e3a8a';
  };

  const loadAvailableObjects = async (reportType) => {
    try {
      const primaryObjectName = reportType.type === 'individual'
        ? reportType.object_name
        : reportType.source_object_name;

      // Load fields for the primary object
      const api = new DataApi(`reportbuilder/objects/${primaryObjectName}/fields`);
      const response = await api.fetchAll();

      if (true) {
        let fields = response.data.data;
        try {
          const fls = await getUserFieldPermissionsByObject();
          const readDenied = getFlsReadDeniedLowerSet(fls, primaryObjectName);
          fields = (fields || []).filter(
            (field) => !readDenied.has(String(field.field_name || '').toLowerCase())
          );
        } catch {
          /* keep unfiltered */
        }

        // Find lookup fields (fields with is_reference = true)
        let lookups = fields.filter(field => field.is_reference && field.reference_object);

        // Get unique reference objects (only objects the user may read)
        let referenceObjects = [...new Set(lookups.map(field => field.reference_object))];
        try {
          const readableLower = await resolveReadableObjectNamesLower();
          if (readableLower != null) {
            referenceObjects = referenceObjects.filter((ref) =>
              canReadObjectForReporting(ref, readableLower)
            );
            lookups = lookups.filter((field) =>
              canReadObjectForReporting(field.reference_object, readableLower)
            );
          }
        } catch {
          /* keep unfiltered */
        }
        setLookupFields(lookups);
        setAvailableObjects(referenceObjects);

        // Create list with only primary and target objects (exclude related objects from lookup fields for now)
        let allObjects = [
          {
            object_name: primaryObjectName,
            object_label: reportType.type === 'individual'
              ? reportType.object_label
              : reportType.source_object_label,
            is_primary: true
          }
        ];

        // For relationship types, include the target object
        if (reportType.type === 'relationship' && reportType.target_object_name) {
          allObjects.push({
            object_name: reportType.target_object_name,
            object_label: reportType.target_object_label || reportType.target_object_name.charAt(0).toUpperCase() + reportType.target_object_name.slice(1),
            is_primary: false
          });
        }

        // Store all objects in the reportType for later use
        reportType.allObjects = allObjects;
        reportType.primaryObject = primaryObjectName;
        reportType.relatedObjects = referenceObjects;
        reportType.lookupFields = lookups;

        console.log('All objects for report:', allObjects);
        console.log('Primary object:', primaryObjectName);
        console.log('Related objects:', referenceObjects);
        console.log('Lookup fields:', lookups);

        return allObjects;
      }
    } catch (error) {
      console.error('Error loading available objects:', error);
      return [];
    }
  };

  const handleReportTypeSelection = async (reportType) => {
    console.log('Selected report type:', reportType);

    // Validate the reportType object
    if (!reportType || typeof reportType !== 'object') {
      console.error('Invalid report type object:', reportType);
      return;
    }

    // Create a clean copy of the reportType to avoid any circular references or invalid properties
    const cleanReportType = {
      id: reportType.id,
      type: reportType.type,
      object_name: reportType.object_name,
      object_label: reportType.object_label,
      source_object_name: reportType.source_object_name,
      source_object_label: reportType.source_object_label,
      target_object_name: reportType.target_object_name,
      target_object_label: reportType.target_object_label,
      relationship_name: reportType.relationship_name,
      relationship_label: reportType.relationship_label,
      relationship_type: reportType.relationship_type,
      source_field_name: reportType.source_field_name,
      target_field_name: reportType.target_field_name,
      description: reportType.description,
      is_standard: reportType.is_standard,
      allow_activity: reportType.allow_activity
    };

    try {
      setSelectedReportType(cleanReportType);

      console.log('Clean report type with field names:', cleanReportType);
      console.log('Source field name:', cleanReportType.source_field_name);
      console.log('Target field name:', cleanReportType.target_field_name);

      await loadAvailableObjects(cleanReportType);
      const lookupGroups = groupLookupFieldsByReference(cleanReportType.lookupFields || []);
      const initialExpanded = {};
      Object.keys(lookupGroups).forEach((key, index) => {
        initialExpanded[key] = index === 0;
      });
      setExpandedLookupGroups(initialExpanded);
    } catch (error) {
      console.error('Error setting selected report type:', error);
      console.error('Report type data:', cleanReportType);
    }
  };

  const handleNext = () => {
    if (selectedReportType) {
      // Get the list of all object names
      const allObjectNames = selectedReportType.allObjects
        ? selectedReportType.allObjects.map(obj => obj.object_name)
        : [];

      // Store the report type, object names, and relationship information
      const reportData = {
        reportType: selectedReportType,
        objectNames: allObjectNames,
        relationships: selectedReportType.relationships || [] // Include relationship information
      };

      sessionStorage.setItem('selectedReportType', JSON.stringify(reportData));
      console.log('Stored report data with object names:', reportData);

      // Navigate to the report builder page
      navigate('/reportbuilderpage');
    }
  };

  const filteredReportTypes = reportTypes.filter((type) => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    const haystack = [
      type.description,
      type.object_label,
      type.object_name,
      type.source_object_label,
      type.source_object_name,
      type.target_object_label,
      type.target_object_name
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(term);
  });

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading report types...</p>
      </div>
    );
  }


  const lookupFieldGroups = groupLookupFieldsByReference(lookupFields);
  const selectedObjectName = selectedReportType?.object_name || selectedReportType?.source_object_name;
  const selectedCategory = selectedReportType ? getReportCategory(selectedReportType) : null;

  return (
    <div className="report-builder-simple">
      <Container fluid className="report-builder-container">
        <header className="report-builder-page-header">
          <div className="report-builder-page-header-text">
            <h1>Report Builder</h1>
            <p>Select the data foundation for your new report.</p>
          </div>
          <button type="button" className="report-builder-cancel-link" onClick={handleCancel}>
            Cancel
          </button>
        </header>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="report-builder-alert">
            {error}
          </Alert>
        )}

        <Row className="report-builder-panels-row g-4">
          <Col lg={5} xl={5}>
            <section className="report-builder-panel report-builder-selection-panel">
              <h3 className="report-builder-panel-title">Select Report Category</h3>
              <div className="report-builder-search-section">
                <FaSearch className="report-builder-search-icon" aria-hidden />
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="report-builder-search-input"
                />
              </div>

              <div className="report-builder-report-types-list">
                {filteredReportTypes.map((reportType, index) => {
                  const objectName = reportType.object_name || reportType.source_object_name || 'default';
                  const IconComponent = getObjectIcon(objectName, reportType);
                  const category = getReportCategory(reportType);
                  const accentColor = getCategoryAccentColor(category.variant);
                  const isSelected = selectedReportType?.id === reportType.id;

                  return (
                    <button
                      type="button"
                      key={`${reportType.id}_${index}`}
                      className={`report-builder-report-type-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleReportTypeSelection(reportType)}
                    >
                      <div
                        className="report-builder-report-type-icon"
                        style={{ color: accentColor, backgroundColor: `${accentColor}14` }}
                      >
                        <IconComponent />
                      </div>
                      <div className="report-builder-report-type-content">
                        <div className="report-builder-report-type-title">
                          {getDisplayTitle(reportType)}
                        </div>
                        <span className={`report-builder-category-badge report-builder-category-badge--${category.variant}`}>
                          {category.label}
                        </span>
                      </div>
                      {isSelected && (
                        <div className="report-builder-selection-indicator" aria-hidden>
                          <FaArrowRight />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {filteredReportTypes.length === 0 && (
                <div className="report-builder-empty-list">
                  <FaDatabase size={40} />
                  <h5>No report types found</h5>
                  <p>{searchTerm ? 'Try adjusting your search terms' : 'No report types available'}</p>
                </div>
              )}
            </section>
          </Col>

          <Col lg={7} xl={7}>
            <section className="report-builder-panel report-builder-details-panel">
              <h3 className="report-builder-panel-title">Selected Report Details</h3>
              {selectedReportType ? (
                <div className="report-builder-selected-details">
                  <div className="report-builder-detail-header">
                    <div
                      className="report-builder-detail-icon"
                      style={{
                        color: getObjectColor(selectedObjectName, selectedReportType),
                        backgroundColor: `${getObjectColor(selectedObjectName, selectedReportType)}14`
                      }}
                    >
                      {(() => {
                        const IconComponent = getObjectIcon(selectedObjectName, selectedReportType);
                        return <IconComponent />;
                      })()}
                    </div>
                    <div>
                      <h4 className="report-builder-detail-title">{getDisplayTitle(selectedReportType)}</h4>
                      <span className={`report-builder-category-badge report-builder-category-badge--${selectedCategory.variant}`}>
                        {selectedCategory.label}
                      </span>
                    </div>
                  </div>

                  <dl className="report-builder-metadata-grid">
                    <div className="report-builder-metadata-item">
                      <dt>Report Type</dt>
                      <dd>
                        {selectedReportType.type === 'individual' ? 'Standard' : 'Joint / Cross-Object'}
                      </dd>
                    </div>
                    {selectedReportType.type === 'individual' ? (
                      <div className="report-builder-metadata-item">
                        <dt>Primary Object</dt>
                        <dd>{selectedReportType.object_label}</dd>
                      </div>
                    ) : (
                      <>
                        <div className="report-builder-metadata-item">
                          <dt>Primary Object</dt>
                          <dd>{selectedReportType.source_object_label}</dd>
                        </div>
                        <div className="report-builder-metadata-item">
                          <dt>Related Object</dt>
                          <dd>{selectedReportType.target_object_label}</dd>
                        </div>
                        {selectedReportType.relationship_name && (
                          <div className="report-builder-metadata-item report-builder-metadata-item--full">
                            <dt>Relationship</dt>
                            <dd>{selectedReportType.relationship_name}</dd>
                          </div>
                        )}
                      </>
                    )}
                  </dl>

                  {availableObjects.length > 0 && (
                    <div className="report-builder-detail-section">
                      <span className="report-builder-section-label">Available Related Objects</span>
                      <div className="available-objects">
                        {availableObjects.map((objectName) => (
                          <span key={objectName} className="object-badge">
                            {objectName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {lookupFields.length > 0 && (
                    <div className="report-builder-detail-section">
                      <span className="report-builder-section-label">Lookup Fields</span>
                      <div className="lookup-fields-tree">
                        {Object.entries(lookupFieldGroups).map(([refObject, fields]) => {
                          const isExpanded = expandedLookupGroups[refObject];
                          return (
                            <div key={refObject} className="lookup-tree-group">
                              <button
                                type="button"
                                className="lookup-tree-group-header"
                                onClick={() => toggleLookupGroup(refObject)}
                                aria-expanded={isExpanded}
                              >
                                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                <span className="lookup-tree-group-name">{refObject} fields</span>
                                <span className="lookup-tree-group-count">{fields.length}</span>
                              </button>
                              {isExpanded && (
                                <ul className="lookup-tree-items">
                                  {fields.map((field) => (
                                    <li key={field.field_name || field.id} className="lookup-tree-item">
                                      <span className="lookup-tree-field-label">{field.field_label}</span>
                                      <span className="lookup-tree-field-ref">{field.reference_object}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="report-builder-detail-footer">
                    <p className="report-builder-detail-description">
                      {selectedReportType.type === 'individual'
                        ? `This report will show data from ${selectedReportType.object_label} only.`
                        : `This report will show data from ${selectedReportType.source_object_label} along with related ${selectedReportType.target_object_label} information.`}
                    </p>
                    <Button
                      variant="primary"
                      onClick={handleNext}
                      className="report-builder-continue-btn"
                    >
                      Continue with this Report Type
                      <FaArrowRight className="ms-2" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="report-builder-no-selection">
                  <FaChartBar size={48} />
                  <h5>Select a Report Type</h5>
                  <p>Choose a report category from the left panel to see details and continue.</p>
                </div>
              )}
            </section>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ReportBuilder;
